import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { combineLatest } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { ConfirmDialogComponent } from 'app/shared/dialog/confirm-dialog.component';
import { Store, StoreEnvironment, StorePermission, StoreStatus, SubscriptionPermission } from 'app/shared/graph';
import { FromNowPipe } from 'app/shared/utilities/from-now.pipe';
import { environment } from 'environments/environment';

import { SetToMigratedDialogComponent, SetToMigratedDialogData, SetToMigratedRegion } from './set-to-migrated-dialog.component';
import { StoresService } from './stores.service';

@Component({
    selector: 'admin-store-status',
    templateUrl: './store-status-page.component.html',
    styleUrls: ['./store-status-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatButton, MatIcon, FormsModule, MatFormField, MatLabel, MatInput, MatHint, MatSelect, MatOption, MatCheckbox, MatIconButton, MatError, SaveButtonComponent, ErrorComponent, DatePipe, FromNowPipe, MatMenu, MatMenuItem, MatMenuTrigger],
})
export class StoreStatusPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly authService = inject(AuthorizationService);
    private readonly storesService = inject(StoresService);
    private readonly matDialog = inject(MatDialog);

    storeId: string;
    authContext?: AuthorizationContext;
    storeWithMetadata?: Store;
    noCommaPattern = /^[^,]*$/;

    environments = [
        { value: StoreEnvironment.Production, display: 'Production (billable, monitored)' },
        { value: StoreEnvironment.Staging, display: 'Staging (non-billable, monitored)' },
        { value: StoreEnvironment.Development, display: 'Development (non-billable, non-monitored)' },
        { value: StoreEnvironment.Demonstration, display: 'Demonstration (non-billable, monitored)' },
        { value: StoreEnvironment.Test, display: 'Test (used for testing)' },
    ];

    statuses = [
        { value: StoreStatus.Active, display: 'Active' },
        { value: StoreStatus.Migrating, display: 'Migrating', disabled: true },
        { value: StoreStatus.Migrated, display: 'Migrated', disabled: true },
        { value: StoreStatus.Closed, display: 'Closed', disabled: true },
        { value: StoreStatus.ScheduledForDeletion, display: 'ScheduledForDeletion', disabled: true },
        { value: StoreStatus.ScheduledForDeletionNotificationSent, display: 'ScheduledForDeletionNotificationSent', disabled: true },
    ];

    regions: SetToMigratedRegion[] = [
        { value: 'eastus', display: 'East US' },
        { value: 'centralus', display: 'Central US' },
        { value: 'westeurope', display: 'West Europe' },
    ].map(region => ({ ...region, disabled: region.value === environment.region }));

    platforms = [
        { value: '', display: 'Unspecified' },
        { value: 'BigCommerce', display: 'BigCommerce' },
        { value: 'Ecwid', display: 'Ecwid' },
        { value: 'Magento', display: 'Magento' },
        { value: 'Prestashop', display: 'Prestashop' },
        { value: 'Shopify', display: 'Shopify' },
        { value: 'Wix', display: 'Wix' },
        { value: 'WooCommerce', display: 'WooCommerce' },
        { value: 'Other', display: 'Other' },
        { value: 'Custom', display: 'In-House (Custom)' },
    ];

    plugins = [
        { value: '', display: 'Unspecified' },
        { value: 'Magento', display: 'Magento' },
        { value: 'Shopify', display: 'Shopify' },
        { value: 'WooCommerce', display: 'WooCommerce' },
        { value: 'None', display: 'None' },
    ];

    get migratedToRegionDisplay(): string {
        const region = this.storeWithMetadata?.migratedToRegion;
        if (!region) {
            return 'Unknown';
        }
        return this.regions.find(entry => entry.value === region)?.display ?? region;
    }

    ngOnInit(): void {
        this.authService.authorizationContext().subscribe(authContext => this.authContext = authContext);
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.subscribeWithGenericLoadingErrorHandling(this.storesService.getStore(this.storeId), (store) => {
                this.storeWithMetadata = store;
            });
        }));
    }

    ngOnDestroy(): void {
        this.unsubscribeSubscriptions();
    }

    deactivate() {
        const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Deactivate store',
                message: `Do you really want to deactivate the store <code>${this.storeId}</code> ?`,
                subMessage: `All data will be deleted after 90 days. This will immediately prevent the store from creating any new orders or projects`,
                options: {
                    yes: {
                        label: 'Deactivate',
                        warn: true,
                    },
                    no: {
                        label: 'Cancel',
                    },
                },
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.subscribeWithGenericSavingErrorHandling(this.storesService.deactivateStore({ id: this.storeId }), () => {
                this.reloadStore();
            });
        });
    }

    save() {
        if (!this.storeWithMetadata) {
            return;
        }

        this.pageStatus = PageStatus.saving;
        const updateStoreMutation = this.storesService.updateStore({
            id: this.storeId,
            label: this.storeWithMetadata.label,
            environment: this.storeWithMetadata.environment,
            status: this.storeWithMetadata.status,
            automaticDesignerUpgrade: this.storeWithMetadata.automaticDesignerUpgrade,
            platform: this.storeWithMetadata.metadata.platform,
            plugin: this.storeWithMetadata.metadata.plugin,
            testDesignerInfo: this.storeWithMetadata.metadata.testDesignerInfo,
            ignoredProblems: this.storeWithMetadata.metadata.ignoredProblems,
        });

        const updateConfigMutation = this.storesService.updateStoreConfig({
            storeId: this.storeId,
            renderingPriority: this.storeWithMetadata.config.renderingPriority,
        });

        this.subscribeWithGenericSavingErrorHandling(
            combineLatest([updateStoreMutation, updateConfigMutation]),
            () => {
                // Both mutations succeeded
            }
        );
    }

    onRenderingPriorityChange(value: number | null | string): void {
        if (!this.storeWithMetadata) {
            return;
        }
        // Convert empty string to null
        if (value === '' || value === null) {
            this.storeWithMetadata.config.renderingPriority = null;
        } else {
            this.storeWithMetadata.config.renderingPriority = typeof value === 'string' ? parseInt(value, 10) : value;
        }
    }

    addDesignerTestInfo() {
        if (!this.storeWithMetadata) {
            return;
        }
        this.storeWithMetadata.metadata.testDesignerInfo.push({});
    }

    removeDesignerTestInfo(i: number) {
        if (!this.storeWithMetadata) {
            return;
        }
        this.storeWithMetadata.metadata.testDesignerInfo.splice(i, 1);
    }

    deactivateForMigration() {
        const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Deactivate store for migration',
                message: `Do you really want to deactivate the store <code>${this.storeId}</code> for migration?`,
                subMessage: `The store will be set to "Migrating" status. Wait 5 minutes for cache expiration before starting the migration process.`,
                options: {
                    yes: {
                        label: 'Deactivate',
                        warn: true,
                    },
                    no: {
                        label: 'Cancel',
                    },
                },
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.subscribeWithGenericSavingErrorHandling(this.storesService.deactivateStoreForMigration({ id: this.storeId }), () => {
                this.reloadStore();
            });
        });
    }

    activateAfterMigration() {
        const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Re-activate store after migration',
                message: `Do you really want to re-activate the store <code>${this.storeId}</code>?`,
                subMessage: '<strong>Warning:</strong> do it only of the migration failed and cannot be fixed.',
                options: {
                    yes: {
                        label: 'Activate',
                        warn: true,
                    },
                    no: {
                        label: 'Cancel',
                    },
                },
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.subscribeWithGenericSavingErrorHandling(this.storesService.activateStoreAfterMigration({ id: this.storeId }), () => {
                this.reloadStore();
            });
        });
    }

    setToMigrated() {
        const dialogRef = this.matDialog.open<SetToMigratedDialogComponent, SetToMigratedDialogData, string>(SetToMigratedDialogComponent,
            {
                data: {
                    storeId: this.storeId,
                    regions: this.regions,
                    selectedRegion: this.storeWithMetadata?.migratedToRegion,
                },
            });
        dialogRef.afterClosed().subscribe((selectedRegion) => {
            if (!selectedRegion) {
                return;
            }

            if (this.storeWithMetadata) {
                this.storeWithMetadata.migratedToRegion = selectedRegion;
            }

            this.subscribeWithGenericSavingErrorHandling(this.storesService.setStoreToMigrated({ id: this.storeId, region: selectedRegion }), () => {
                this.reloadStore();
            });
        });
    }

    canDeactivateSubscription(subscriptionId: string) {
        return this.authContext?.hasSubscriptionPermissions(subscriptionId, SubscriptionPermission.ManageSubscriptionMetadata);
    }

    canMigrateStore(subscriptionId: string) {
        return this.authContext?.hasStorePermissions(this.storeId, subscriptionId, StorePermission.MigrateStore);
    }

    private reloadStore() {
        this.subscribeWithGenericLoadingErrorHandling(this.storesService.getStore(this.storeId), (store) => {
            this.storeWithMetadata = store;
        });
    }
}
